import type { SignerConfig } from '../types/signers';
import type { EncryptConfig, EncryptResult, DecryptConfig } from './types';
import { emblemPost } from '../signers/http';
import { validateSignerConfig } from '../signers/validation';
import { fetchVaultInfo } from '../signers/vault';

export type { EncryptConfig, EncryptResult, DecryptConfig } from './types';
export type { DecryptResult, DecryptBinaryResult } from './types';

/** Binary marker prefix used to distinguish binary data from plain text */
const BINARY_MARKER = 'b64:';

/** Response shape from POST /encrypt */
interface EncryptApiResponse {
  success: boolean;
  ciphertext: string;
  dataToEncryptHash: string;
}

/** Response shape from POST /decrypt */
interface DecryptApiResponse {
  success: boolean;
  decryptedString: string;
}

/**
 * Resolve vault address and tokenId from the signer config.
 * Uses fetchVaultInfo to retrieve the vault's EVM address and vaultId.
 */
async function resolveVaultDetails(
  config: SignerConfig
): Promise<{ address: string; tokenId: string }> {
  const vaultInfo = await fetchVaultInfo(config);

  if (!vaultInfo.evmAddress) {
    throw new Error('Could not resolve vault EVM address');
  }
  if (!vaultInfo.vaultId) {
    throw new Error('Could not resolve vault tokenId');
  }

  return {
    address: vaultInfo.evmAddress,
    tokenId: vaultInfo.vaultId,
  };
}

/**
 * Encrypt a string using the Emblem encryption service.
 *
 * If `options.address` or `options.tokenId` are not provided, they will be
 * resolved automatically from the authenticated vault info.
 *
 * @param data - The plaintext string to encrypt
 * @param options - Encryption configuration including auth config
 * @returns The encrypted ciphertext and data hash (needed for decryption)
 */
export async function encrypt(data: string, options: EncryptConfig): Promise<EncryptResult> {
  if (!data) {
    throw new Error('Data to encrypt must be a non-empty string');
  }
  if (!options || !options.config) {
    throw new Error('EncryptConfig with a valid config is required');
  }

  validateSignerConfig(options.config);

  let { address, tokenId } = options;

  // Resolve address/tokenId from vault if not explicitly provided
  if (!address || !tokenId) {
    const vaultDetails = await resolveVaultDetails(options.config);
    address = address ?? vaultDetails.address;
    tokenId = tokenId ?? vaultDetails.tokenId;
  }

  const response = await emblemPost<EncryptApiResponse>('/encrypt', {
    text: data,
    address,
    tokenId,
  }, options.config);

  if (!response || !response.ciphertext || !response.dataToEncryptHash) {
    throw new Error('Invalid encrypt response: missing ciphertext or dataToEncryptHash');
  }

  return {
    ciphertext: response.ciphertext,
    dataToEncryptHash: response.dataToEncryptHash,
  };
}

/**
 * Encrypt binary data (Uint8Array) using the Emblem encryption service.
 *
 * The binary data is Base64-encoded with a marker prefix so that decryptBinary()
 * can distinguish it from plain text and decode it back to a Uint8Array.
 *
 * @param data - The binary data to encrypt
 * @param options - Encryption configuration including auth config
 * @returns The encrypted ciphertext and data hash (needed for decryption)
 */
export async function encryptBinary(
  data: Uint8Array,
  options: EncryptConfig
): Promise<EncryptResult> {
  if (!data || !(data instanceof Uint8Array)) {
    throw new Error('Data must be a Uint8Array');
  }

  // Base64-encode the binary data and prepend the marker
  const base64 = uint8ArrayToBase64(data);
  const encoded = `${BINARY_MARKER}${base64}`;

  return encrypt(encoded, options);
}

/**
 * Decrypt ciphertext using the Emblem decryption service.
 *
 * If `options.address` or `options.tokenId` are not provided, they will be
 * resolved automatically from the authenticated vault info.
 *
 * If the decrypted string starts with the binary marker ("b64:"), the marker is
 * stripped and the raw Base64 string is returned.
 *
 * @param options - Decryption configuration including ciphertext and hash
 * @returns The decrypted plaintext string
 */
export async function decrypt(options: DecryptConfig): Promise<string> {
  if (!options || !options.config) {
    throw new Error('DecryptConfig with a valid config is required');
  }
  if (!options.ciphertext) {
    throw new Error('ciphertext is required for decryption');
  }
  if (!options.dataToEncryptHash) {
    throw new Error('dataToEncryptHash is required for decryption');
  }

  validateSignerConfig(options.config);

  let { address, tokenId } = options;

  // Resolve address/tokenId from vault if not explicitly provided
  if (!address || !tokenId) {
    const vaultDetails = await resolveVaultDetails(options.config);
    address = address ?? vaultDetails.address;
    tokenId = tokenId ?? vaultDetails.tokenId;
  }

  const response = await emblemPost<DecryptApiResponse>('/decrypt', {
    address,
    tokenId,
    ciphertext: options.ciphertext,
    dataToEncryptHash: options.dataToEncryptHash,
  }, options.config);

  if (!response || typeof response.decryptedString !== 'string') {
    throw new Error('Invalid decrypt response: missing decryptedString');
  }

  let result = response.decryptedString;

  // Strip binary marker if present (for users who just want the base64 string)
  if (result.startsWith(BINARY_MARKER)) {
    result = result.slice(BINARY_MARKER.length);
  }

  return result;
}

/**
 * Decrypt ciphertext and return the result as a Uint8Array.
 *
 * If the decrypted data was originally encrypted with encryptBinary(), the
 * binary marker is detected and the Base64 payload is decoded back to bytes.
 * Otherwise the decrypted string is encoded as UTF-8 bytes as a fallback.
 *
 * @param options - Decryption configuration including ciphertext and hash
 * @returns The decrypted binary data as a Uint8Array
 */
export async function decryptBinary(options: DecryptConfig): Promise<Uint8Array> {
  if (!options || !options.config) {
    throw new Error('DecryptConfig with a valid config is required');
  }
  if (!options.ciphertext) {
    throw new Error('ciphertext is required for decryption');
  }
  if (!options.dataToEncryptHash) {
    throw new Error('dataToEncryptHash is required for decryption');
  }

  validateSignerConfig(options.config);

  // Perform decryption using the raw API to check the marker ourselves
  let { address, tokenId } = options;

  if (!address || !tokenId) {
    const vaultDetails = await resolveVaultDetails(options.config);
    address = address ?? vaultDetails.address;
    tokenId = tokenId ?? vaultDetails.tokenId;
  }

  const response = await emblemPost<DecryptApiResponse>('/decrypt', {
    address,
    tokenId,
    ciphertext: options.ciphertext,
    dataToEncryptHash: options.dataToEncryptHash,
  }, options.config);

  if (!response || typeof response.decryptedString !== 'string') {
    throw new Error('Invalid decrypt response: missing decryptedString');
  }

  const decrypted = response.decryptedString;

  // If the binary marker is present, decode the Base64 payload
  if (decrypted.startsWith(BINARY_MARKER)) {
    const base64 = decrypted.slice(BINARY_MARKER.length);
    return base64ToUint8Array(base64);
  }

  // Fallback: encode the plain string as UTF-8 bytes
  return new TextEncoder().encode(decrypted);
}

/* ------------------------------------------------------------------ */
/*  Base64 helpers (environment-agnostic: works in Node and browsers)  */
/* ------------------------------------------------------------------ */

function uint8ArrayToBase64(bytes: Uint8Array): string {
  // Use btoa if available (browsers + Node 16+)
  if (typeof btoa === 'function') {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // Node.js Buffer fallback
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  }

  throw new Error('No Base64 encoding method available in this environment');
}

function base64ToUint8Array(base64: string): Uint8Array {
  // Use atob if available (browsers + Node 16+)
  if (typeof atob === 'function') {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  // Node.js Buffer fallback
  if (typeof Buffer !== 'undefined') {
    const buf = Buffer.from(base64, 'base64');
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  throw new Error('No Base64 decoding method available in this environment');
}
